import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sparkles, Save, Settings, User, Backpack, LogOut, Heart, Coins, Pencil, X, Upload, ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMonsterGif, getMonsterGifByMonster, getMonsterFallbackGif, getMonsterDisplayName, calculateXPNeeded } from '../utils/helpers';
import { studentAPI } from '../utils/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default trainer avatar (Pokémon Let's Go style, transparent PNG) — used
// when the student has not uploaded their own picture yet.
const DEFAULT_TRAINER_AVATAR = '/map-assets/avatar.png';

/**
 * HP/PP styled status bar (Pokémon classic look).
 * - "xp" variant uses blue gradient
 * - "coins" variant uses amber gradient
 */
const StatusBar = ({ value, max, variant = 'xp', label, valueLabel, testId }) => {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const fillGradient =
    variant === 'xp'
      ? 'linear-gradient(90deg, #4facfe, #00f2fe)'
      : 'linear-gradient(90deg, #facc15, #f97316)';
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className="font-outfit font-black text-[10px] tracking-widest text-slate-100 w-9">{label}</span>
      <div className="flex-1 h-3.5 bg-slate-900 rounded-full border-2 border-slate-900 overflow-hidden relative shadow-inner">
        <div
          className={`h-full rounded-full transition-[width] duration-700 ease-out relative ${variant === 'xp' ? 'aura-shimmer' : ''}`}
          style={{ width: `${pct}%`, backgroundImage: fillGradient }}
        />
      </div>
      <span className="font-outfit font-black text-[10px] tracking-wider text-slate-50 whitespace-nowrap min-w-[42px] text-right">
        {valueLabel}
      </span>
    </div>
  );
};

const InlineEdit = ({ label, value, onSave, placeholder, testId }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => { setDraft(value || ''); }, [value]);

  const handleSave = async () => {
    if (draft.trim() === (value || '').trim()) {
      setEditing(false);
      return;
    }
    const ok = await onSave(draft.trim());
    if (ok) setEditing(false);
  };

  return (
    <div className="flex flex-col gap-1" data-testid={`trainer-edit-${testId}`}>
      <span className="font-outfit font-black text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      {editing ? (
        <div className="flex items-center gap-2">
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
            placeholder={placeholder}
            maxLength={30}
            className="h-8 text-sm font-nunito"
            data-testid={`trainer-edit-input-${testId}`}
          />
          <Button size="icon" className="h-8 w-8 shrink-0" onClick={handleSave} data-testid={`trainer-edit-save-${testId}`}>
            <Save className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setEditing(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="group flex items-center justify-between gap-2 text-sm font-nunito font-bold text-slate-900 dark:text-slate-100 bg-slate-100 dark:bg-slate-800 hover:bg-amber-100 dark:hover:bg-slate-700 rounded-lg px-3 py-1.5 border-2 border-slate-300 dark:border-slate-700 transition-colors"
          data-testid={`trainer-edit-trigger-${testId}`}
        >
          <span className="truncate">{value || <span className="italic text-slate-400">{placeholder}</span>}</span>
          <Pencil className="w-3.5 h-3.5 text-slate-400 group-hover:text-amber-600" />
        </button>
      )}
    </div>
  );
};

export const TrainerCard = ({ student, onReload, gifCacheReady }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [openProfile, setOpenProfile] = useState(false);
  const [openBag, setOpenBag] = useState(false);
  const [popMenu, setPopMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Allow other components (e.g. QuickMenu) to open the backpack dialog
  // via a custom window event — keeps state ownership inside TrainerCard.
  useEffect(() => {
    const handler = () => setOpenBag(true);
    window.addEventListener('open-trainer-backpack', handler);
    return () => window.removeEventListener('open-trainer-backpack', handler);
  }, []);

  if (!student) return null;

  const monster = student.monster;
  const level = student.level || 1;
  const xp = student.xp || 0;
  const xpNeeded = calculateXPNeeded(level);
  const coins = student.coins || 0;
  const monsterName = monster ? getMonsterDisplayName(monster) : 'Sem Pokémon';
  const monsterGif = monster ? getMonsterGifByMonster(monster) : null;
  const trainerYear = student.school_year || '';
  const pokeType = monster?.pokemon_type || '';
  const pokeWeak = monster?.pokemon_weakness || '';
  const trainerName = student.user_name || 'Treinador';
  const avatarUrl = student.avatar_url || DEFAULT_TRAINER_AVATAR;

  const saveTraits = async (patch) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/student/monster/traits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao salvar');
      }
      toast.success('Salvo!', { icon: '✨' });
      onReload && onReload();
      return true;
    } catch (e) {
      toast.error(e.message || 'Erro ao salvar');
      return false;
    }
  };

  const handleLogout = async () => {
    setPopMenu(false);
    try { await logout(); navigate('/login'); } catch (e) { console.error(e); }
  };

  // Convert uploaded image to base64 and persist via /traits endpoint.
  const handleAvatarUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem.');
      return;
    }
    if (file.size > 1_000_000) {
      toast.error('Imagem muito grande. Use até 1MB.');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const ok = await saveTraits({ avatar_url: dataUrl });
      if (ok) toast.success('Avatar atualizado!', { icon: '🎨' });
    } catch (e) {
      toast.error('Falha ao carregar imagem.');
    } finally {
      setUploading(false);
    }
  };

  const handleResetAvatar = async () => {
    const ok = await saveTraits({ avatar_url: '' });
    if (ok) toast.success('Avatar voltou ao padrão.');
  };

  return (
    <>
      {/* Compact pinned card (top-left of map) */}
      <div
        data-testid="trainer-card"
        className="absolute top-3 left-3 sm:top-4 sm:left-4 z-40 bg-white/95 backdrop-blur-md border-[3px] border-slate-900 rounded-2xl shadow-[4px_4px_0_rgba(15,23,42,0.85)] p-2 sm:p-3 flex items-center gap-2 sm:gap-3 max-w-[280px] sm:max-w-[340px]"
      >
        {/* Avatar — opens profile menu */}
        <Popover open={popMenu} onOpenChange={setPopMenu}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="relative shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-indigo-500 to-violet-700 border-[3px] border-slate-900 overflow-hidden shadow-md hover:scale-105 transition-transform"
              data-testid="trainer-card-avatar"
              aria-label="Menu de perfil"
            >
              <img
                src={avatarUrl}
                alt={trainerName}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = DEFAULT_TRAINER_AVATAR; }}
              />
              <span className="absolute -bottom-1.5 -right-1.5 bg-amber-400 text-slate-900 font-black text-[10px] px-1.5 py-0.5 rounded-full border-2 border-slate-900 leading-none">
                Lv{level}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            data-testid="trainer-profile-popover"
            align="start"
            sideOffset={10}
            className="w-56 p-2 border-[3px] border-slate-900 rounded-2xl shadow-[4px_4px_0_rgba(15,23,42,0.85)] bg-white"
          >
            <button
              type="button"
              onClick={() => { setPopMenu(false); setOpenProfile(true); }}
              data-testid="trainer-menu-profile"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-amber-100 font-outfit font-bold text-sm text-slate-900 transition-colors"
            >
              <User className="w-4 h-4 text-indigo-600" /> Perfil do Treinador
            </button>
            <button
              type="button"
              onClick={() => { setPopMenu(false); fileInputRef.current?.click(); }}
              data-testid="trainer-menu-change-avatar"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-amber-100 font-outfit font-bold text-sm text-slate-900 transition-colors"
            >
              <ImageIcon className="w-4 h-4 text-indigo-600" /> Trocar Avatar
            </button>
            <button
              type="button"
              onClick={() => { setPopMenu(false); navigate('/student/dashboard'); }}
              data-testid="trainer-menu-settings"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-amber-100 font-outfit font-bold text-sm text-slate-900 transition-colors"
            >
              <Settings className="w-4 h-4 text-indigo-600" /> Configurações
            </button>
            <div className="my-1 h-px bg-slate-200" />
            <button
              type="button"
              onClick={handleLogout}
              data-testid="trainer-menu-logout"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-rose-50 font-outfit font-bold text-sm text-rose-700 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </PopoverContent>
        </Popover>

        {/* Status bars (HP-like XP + PP-like Coins) */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5 bg-slate-900 rounded-xl px-2.5 py-1.5 border-2 border-slate-900">
          <div className="flex items-center justify-between gap-2">
            <span className="font-outfit font-black text-[11px] text-amber-200 truncate max-w-[100px] sm:max-w-[140px]">{trainerName}</span>
            <button
              type="button"
              onClick={() => setOpenProfile(true)}
              className="text-[10px] font-outfit font-black tracking-widest text-amber-300 hover:text-amber-100 transition-colors"
              data-testid="trainer-card-expand"
            >
              VER MAIS
            </button>
          </div>
          <StatusBar value={xp} max={xpNeeded} variant="xp" label="AURA" valueLabel={`${xp}/${xpNeeded}`} testId="trainer-xp-bar" />
          <StatusBar value={coins} max={Math.max(coins, 500)} variant="coins" label="$" valueLabel={`${coins}`} testId="trainer-coins-bar" />
        </div>

        {/* Backpack button (right side) — opens bag inventory.
            (The Pokémon sprite now lives inside the expanded profile dialog.) */}
        <button
          type="button"
          onClick={() => setOpenBag(true)}
          className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-300 to-orange-400 border-[3px] border-slate-900 shadow-md hover:scale-105 active:scale-95 transition-transform overflow-hidden grid place-items-center"
          data-testid="trainer-card-backpack"
          aria-label="Abrir mochila"
          title="Mochila"
        >
          <Backpack className="w-7 h-7 sm:w-8 sm:h-8 text-slate-900 drop-shadow-sm" strokeWidth={2.5} />
        </button>
      </div>

      {/* Hidden file input for avatar upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="trainer-avatar-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          handleAvatarUpload(f);
          e.target.value = '';
        }}
      />

      {/* Expanded profile dialog */}
      <Dialog open={openProfile} onOpenChange={setOpenProfile}>
        <DialogContent className="max-w-md border-[3px] border-slate-900 shadow-[6px_6px_0_rgba(15,23,42,0.85)]" data-testid="trainer-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl tracking-tight">{trainerName}</DialogTitle>
            <DialogDescription className="font-nunito text-xs text-slate-500">
              Personalize seu treinador e seu Pokémon — só você vê essas escolhas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Avatar big preview + upload controls */}
            <div className="flex items-center gap-4 bg-gradient-to-br from-indigo-50 to-violet-100 border-2 border-indigo-300 rounded-xl p-3">
              <img
                src={avatarUrl}
                alt={trainerName}
                onError={(e) => { e.currentTarget.src = DEFAULT_TRAINER_AVATAR; }}
                className="w-20 h-20 rounded-full object-cover border-[3px] border-slate-900 shadow-md"
                data-testid="trainer-profile-avatar-preview"
              />
              <div className="flex-1 min-w-0">
                <p className="font-outfit font-black text-lg text-slate-900 truncate">{trainerName}</p>
                <p className="font-nunito text-xs text-slate-600">Nível {level} · Treinador</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="h-7 text-xs font-outfit font-bold"
                    data-testid="trainer-profile-upload-btn"
                  >
                    <Upload className="w-3 h-3 mr-1" /> {uploading ? 'Enviando…' : 'Enviar Imagem'}
                  </Button>
                  {student.avatar_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleResetAvatar}
                      className="h-7 text-xs font-outfit font-bold"
                      data-testid="trainer-profile-reset-btn"
                    >
                      Restaurar Padrão
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Monster summary */}
            <div className="flex items-center gap-4 bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300 rounded-xl p-3">
              {monsterGif && (
                <img
                  src={monsterGif}
                  alt={monsterName}
                  loading="lazy"
                  className="w-20 h-20 object-contain drop-shadow-md"
                  onError={(e) => { if (!e.currentTarget.dataset.fb && monster) { e.currentTarget.dataset.fb = '1'; e.currentTarget.src = getMonsterFallbackGif(monster); } }}
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-outfit font-black text-lg text-slate-900 truncate">{monsterName}</p>
                <p className="font-nunito text-xs text-slate-600">
                  Lv {level} · {monster?.class_type ? monster.class_type.toUpperCase() : '—'} · Estágio {monster?.stage || 1}
                </p>
              </div>
            </div>

            <InlineEdit
              label="Ano Escolar do Treinador"
              value={trainerYear}
              onSave={(v) => saveTraits({ school_year: v })}
              placeholder="Ex: 7º ano"
              testId="school-year"
            />
            <InlineEdit
              label={`Tipo de ${monsterName}`}
              value={pokeType}
              onSave={(v) => saveTraits({ pokemon_type: v })}
              placeholder="Ex: Água, Fogo, Elétrico…"
              testId="pokemon-type"
            />
            <InlineEdit
              label={`Fraqueza de ${monsterName}`}
              value={pokeWeak}
              onSave={(v) => saveTraits({ pokemon_weakness: v })}
              placeholder="Ex: Planta, Pedra…"
              testId="pokemon-weakness"
            />

            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="bg-slate-900 text-white rounded-xl p-3 border-2 border-slate-900">
                <div className="flex items-center gap-1 text-[10px] font-outfit font-black tracking-widest text-cyan-300">
                  <Heart className="w-3 h-3" /> AURA
                </div>
                <p className="font-outfit font-black text-xl mt-1">{xp}<span className="text-xs text-slate-300">/{xpNeeded}</span></p>
              </div>
              <div className="bg-slate-900 text-white rounded-xl p-3 border-2 border-slate-900">
                <div className="flex items-center gap-1 text-[10px] font-outfit font-black tracking-widest text-amber-300">
                  <Coins className="w-3 h-3" /> COINS
                </div>
                <p className="font-outfit font-black text-xl mt-1">{coins}</p>
              </div>
            </div>

            <div className="text-center text-xs text-slate-500 italic font-nunito pt-1">
              <Sparkles className="inline w-3 h-3 mr-1" />
              Personalize seu Pokémon — só você vê essas escolhas.
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Backpack dialog — uses the existing MochilaMenu lazily */}
      <Dialog open={openBag} onOpenChange={setOpenBag}>
        <DialogContent
          data-testid="trainer-backpack-dialog"
          className="max-w-2xl max-h-[85vh] overflow-y-auto border-[3px] border-slate-900 shadow-[6px_6px_0_rgba(15,23,42,0.85)]"
        >
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl flex items-center gap-2">
              <Backpack className="w-6 h-6 text-amber-600" /> Mochila do Treinador
            </DialogTitle>
            <DialogDescription className="font-nunito text-xs text-slate-500">
              Itens coletados em batalhas e missões.
            </DialogDescription>
          </DialogHeader>
          <BackpackContent onClose={() => setOpenBag(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

/* Interactive inventory view — fetches inventory and lets the student USE items.
 * Mirrors the behaviour of MochilaMenu but in a compact grid suitable for the
 * Trainer Card backpack dialog.
 */
const BackpackContent = ({ onClose }) => {
  const [items, setItems] = useState(null);
  const [usingId, setUsingId] = useState(null);

  const fetchItems = async () => {
    try {
      const r = await studentAPI.getInventory?.();
      setItems(r?.data?.items || []);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { fetchItems(); }, []);

  const handleUse = async (item) => {
    if (usingId) return;
    setUsingId(item.inventory_id);
    try {
      const r = await studentAPI.useItem(item.inventory_id);
      toast.success(r.data?.message || 'Item usado!', { icon: item.emoji, duration: 3500 });
      // optimistically remove from local list
      setItems((prev) => (prev || []).filter((i) => i.inventory_id !== item.inventory_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao usar item');
    } finally {
      setUsingId(null);
    }
  };

  if (items === null) {
    return <div className="py-8 text-center text-slate-500 font-nunito">Abrindo mochila…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="py-10 text-center">
        <Backpack className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="font-outfit font-bold text-slate-700">Sua mochila está vazia</p>
        <p className="text-sm text-slate-500 font-nunito">Visite o Mercado para comprar itens úteis.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2" data-testid="backpack-items-grid">
      {items.map((it, i) => {
        const isUsing = usingId === it.inventory_id;
        return (
          <div
            key={`${it.inventory_id || i}`}
            className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300 rounded-xl p-3 text-center flex flex-col"
          >
            <div className="text-4xl leading-none">{it.emoji || it.icon || '📦'}</div>
            <p className="font-outfit font-black text-sm mt-1 text-slate-900 truncate" title={it.name}>{it.name}</p>
            {it.description && (
              <p className="font-nunito text-[10px] text-slate-600 line-clamp-2 mb-2">{it.description}</p>
            )}
            <button
              type="button"
              data-testid={`backpack-use-${it.item_type}`}
              disabled={isUsing || usingId !== null}
              onClick={() => handleUse(it)}
              className={`mt-auto py-1.5 rounded-lg text-xs font-outfit font-black uppercase tracking-widest transition-all ${
                isUsing
                  ? 'bg-slate-300 text-slate-500 cursor-wait'
                  : 'bg-slate-900 text-white hover:bg-slate-800 active:translate-y-0.5'
              }`}
            >
              {isUsing ? 'Usando…' : 'Usar'}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default TrainerCard;
